import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { PhoneIcon, MailIcon, ExternalLinkIcon, CheckCircleIcon, ChevronDownIcon, TrendingDownIcon, DollarSignIcon, HomeIcon, SearchIcon, XIcon, FilterIcon, FileTextIcon, CheckIcon } from 'lucide-react';
const CURRENT_MARKET_RATE = 6.25;
interface Borrower {
  id: string;
  name: string;
  loanAmount: number;
  rate: number;
  fundedDate: string;
  status: 'funded' | 'contacted';
  phone: string;
  email: string;
  propertyValue: number;
  loanTerm: number;
  loanType: 'Conventional' | 'FHA' | 'VA' | 'Jumbo';
}
const INITIAL_BORROWERS: Borrower[] = [{
  id: '1',
  name: 'Sarah Johnson',
  loanAmount: 450000,
  rate: 6.875,
  fundedDate: '2023-11-15',
  status: 'funded',
  phone: '(555) 123-4567',
  email: 'sarah.j@example.com',
  propertyValue: 575000,
  loanTerm: 30,
  loanType: 'Conventional'
}, {
  id: '2',
  name: 'Michael Chen',
  loanAmount: 325000,
  rate: 7.125,
  fundedDate: '2023-10-22',
  status: 'funded',
  phone: '(555) 987-6543',
  email: 'm.chen@example.com',
  propertyValue: 410000,
  loanTerm: 30,
  loanType: 'FHA'
}, {
  id: '3',
  name: 'Emily Davis',
  loanAmount: 550000,
  rate: 6.5,
  fundedDate: '2023-12-01',
  status: 'contacted',
  phone: '(555) 456-7890',
  email: 'emily.d@example.com',
  propertyValue: 720000,
  loanTerm: 30,
  loanType: 'Conventional'
}, {
  id: '4',
  name: 'Robert Wilson',
  loanAmount: 280000,
  rate: 7.25,
  fundedDate: '2023-09-15',
  status: 'funded',
  phone: '(555) 234-5678',
  email: 'r.wilson@example.com',
  propertyValue: 350000,
  loanTerm: 30,
  loanType: 'VA'
}, {
  id: '5',
  name: 'Jessica Martinez',
  loanAmount: 620000,
  rate: 6.99,
  fundedDate: '2023-11-30',
  status: 'funded',
  phone: '(555) 876-5432',
  email: 'j.martinez@example.com',
  propertyValue: 810000,
  loanTerm: 30,
  loanType: 'Conventional'
}, {
  id: '6',
  name: 'David Thompson',
  loanAmount: 750000,
  rate: 7.5,
  fundedDate: '2023-08-10',
  status: 'funded',
  phone: '(555) 345-6789',
  email: 'd.thompson@example.com',
  propertyValue: 950000,
  loanTerm: 30,
  loanType: 'Jumbo'
}, {
  id: '7',
  name: 'Jennifer Wu',
  loanAmount: 420000,
  rate: 6.375,
  fundedDate: '2023-06-15',
  status: 'contacted',
  phone: '(555) 678-9012',
  email: 'j.wu@example.com',
  propertyValue: 550000,
  loanTerm: 30,
  loanType: 'Conventional'
}, {
  id: '8',
  name: 'James Rodriguez',
  loanAmount: 290000,
  rate: 7.125,
  fundedDate: '2023-12-20',
  status: 'funded',
  phone: '(555) 901-2345',
  email: 'j.rodriguez@example.com',
  propertyValue: 380000,
  loanTerm: 30,
  loanType: 'FHA'
}, {
  id: '9',
  name: 'Lisa Anderson',
  loanAmount: 510000,
  rate: 6.75,
  fundedDate: '2023-05-01',
  status: 'funded',
  phone: '(555) 234-5678',
  email: 'l.anderson@example.com',
  propertyValue: 650000,
  loanTerm: 30,
  loanType: 'Conventional'
}, {
  id: '10',
  name: 'Kevin Pat',
  loanAmount: 680000,
  rate: 7.375,
  fundedDate: '2023-09-05',
  status: 'funded',
  phone: '(555) 567-8901',
  email: 'k.patel@example.com',
  propertyValue: 850000,
  loanTerm: 30,
  loanType: 'Jumbo'
}, {
  id: '11',
  name: 'Amanda Lewis',
  loanAmount: 340000,
  rate: 6.625,
  fundedDate: '2023-07-20',
  status: 'contacted',
  phone: '(555) 890-1234',
  email: 'a.lewis@example.com',
  propertyValue: 450000,
  loanTerm: 30,
  loanType: 'VA'
}, {
  id: '12',
  name: 'Thomas Wright',
  loanAmount: 210000,
  rate: 7.0,
  fundedDate: '2023-11-05',
  status: 'funded',
  phone: '(555) 123-4567',
  email: 't.wright@example.com',
  propertyValue: 280000,
  loanTerm: 30,
  loanType: 'FHA'
}];
function calcMonthlyPayment(principal: number, annualRate: number, years: number) {
  const r = annualRate / 100 / 12;
  const n = years * 12;
  return principal * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
}
function getTalkingPoints(borrower: Borrower): {
  icon: React.ReactNode;
  title: string;
  detail: string;
}[] {
  const points: {
    icon: React.ReactNode;
    title: string;
    detail: string;
  }[] = [];
  const rateDiff = borrower.rate - CURRENT_MARKET_RATE;
  // 1. Rate & monthly savings
  if (rateDiff > 0) {
    const currentPayment = calcMonthlyPayment(borrower.loanAmount, borrower.rate, borrower.loanTerm);
    const newPayment = calcMonthlyPayment(borrower.loanAmount, CURRENT_MARKET_RATE, borrower.loanTerm);
    const monthlySavings = currentPayment - newPayment;
    const yearlySavings = monthlySavings * 12;
    points.push({
      icon: <TrendingDownIcon className="w-4 h-4 text-green-600" data-id="element-1" />,
      title: `Rate reduction of ${rateDiff.toFixed(3)}%`,
      detail: `Moving from ${borrower.rate.toFixed(3)}% to today's ${CURRENT_MARKET_RATE.toFixed(2)}% rate could save ${formatUSD(Math.round(monthlySavings))}/mo (${formatUSD(Math.round(yearlySavings))}/yr).`
    });
  }
  // 2. Monthly payment savings in dollar terms
  if (rateDiff > 0.25) {
    const currentPayment = calcMonthlyPayment(borrower.loanAmount, borrower.rate, borrower.loanTerm);
    const lifetimeSavings = (currentPayment - calcMonthlyPayment(borrower.loanAmount, CURRENT_MARKET_RATE, borrower.loanTerm)) * borrower.loanTerm * 12;
    points.push({
      icon: <DollarSignIcon className="w-4 h-4 text-blue-600" data-id="element-2" />,
      title: `${formatUSD(Math.round(lifetimeSavings))} in potential lifetime savings`,
      detail: `On a ${formatUSD(borrower.loanAmount)} loan over ${borrower.loanTerm} years, the rate difference adds up significantly — a strong reason to act now.`
    });
  } else if (rateDiff <= 0) {
    points.push({
      icon: <DollarSignIcon className="w-4 h-4 text-blue-600" data-id="element-3" />,
      title: 'Cash-out refinance opportunity',
      detail: `Even at a similar rate, ${borrower.name.split(' ')[0]} could tap into their home equity for renovations, debt consolidation, or other financial goals.`
    });
  }
  // 3. Equity position
  const ltv = borrower.loanAmount / borrower.propertyValue * 100;
  const estimatedEquity = borrower.propertyValue - borrower.loanAmount;
  const monthsSinceFunding = Math.max(1, Math.round((Date.now() - new Date(borrower.fundedDate).getTime()) / (1000 * 60 * 60 * 24 * 30)));
  const appreciationEstimate = borrower.propertyValue * 0.04 * (monthsSinceFunding / 12);
  points.push({
    icon: <HomeIcon className="w-4 h-4 text-purple-600" data-id="element-4" />,
    title: `~${formatUSD(Math.round(estimatedEquity + appreciationEstimate))} estimated equity`,
    detail: `With an original LTV of ${ltv.toFixed(0)}% and ~${monthsSinceFunding} months of payments plus estimated appreciation, ${borrower.name.split(' ')[0]} has a strong equity position.`
  });
  return points.slice(0, 3);
}
function formatUSD(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
}
function generateCallScript(borrower: Borrower): {
  section: string;
  text: string;
}[] {
  const firstName = borrower.name.split(' ')[0];
  const rateDiff = borrower.rate - CURRENT_MARKET_RATE;
  const monthsSinceFunding = Math.max(1, Math.round((Date.now() - new Date(borrower.fundedDate).getTime()) / (1000 * 60 * 60 * 24 * 30)));
  const currentPayment = calcMonthlyPayment(borrower.loanAmount, borrower.rate, borrower.loanTerm);
  const newPayment = calcMonthlyPayment(borrower.loanAmount, CURRENT_MARKET_RATE, borrower.loanTerm);
  const monthlySavings = currentPayment - newPayment;
  const yearlySavings = monthlySavings * 12;
  const ltv = borrower.loanAmount / borrower.propertyValue * 100;
  const estimatedEquity = borrower.propertyValue - borrower.loanAmount;
  const appreciationEstimate = borrower.propertyValue * 0.04 * (monthsSinceFunding / 12);
  const totalEquity = estimatedEquity + appreciationEstimate;
  const script: {
    section: string;
    text: string;
  }[] = [];
  // Opening
  script.push({
    section: 'Opening',
    text: `Hi ${firstName}, this is John — I was your loan officer when we closed on your home back in ${new Date(borrower.fundedDate).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })}. How have you been? I hope you're enjoying the home!`
  });
  // Reason for call
  script.push({
    section: 'Reason for Call',
    text: `The reason I'm reaching out is that I like to keep an eye on the market for my past clients, and I noticed some changes that could really benefit you. I wanted to make sure you had the information to make the best decision for your situation.`
  });
  // Rate talking point
  if (rateDiff > 0) {
    script.push({
      section: 'Rate Opportunity',
      text: `When we closed your loan, your rate was ${borrower.rate.toFixed(3)}%. Right now, rates have come down to around ${CURRENT_MARKET_RATE.toFixed(2)}% — that's a ${rateDiff.toFixed(3)}% reduction. On your ${formatUSD(borrower.loanAmount)} loan, that could save you about ${formatUSD(Math.round(monthlySavings))} per month, which adds up to ${formatUSD(Math.round(yearlySavings))} a year. That's real money back in your pocket.`
    });
  } else {
    script.push({
      section: 'Cash-Out Opportunity',
      text: `Even though rates are similar to when we closed, there's actually a great opportunity here. With the equity you've built up in your home, a cash-out refinance could give you access to funds for renovations, paying off higher-interest debt, or other financial goals — all while keeping a competitive rate.`
    });
  }
  // Lifetime savings (if significant rate diff)
  if (rateDiff > 0.25) {
    const lifetimeSavings = (currentPayment - newPayment) * borrower.loanTerm * 12;
    script.push({
      section: 'Long-Term Impact',
      text: `And if we look at the bigger picture over the life of your ${borrower.loanTerm}-year loan, that rate difference could save you around ${formatUSD(Math.round(lifetimeSavings))} total. I always want my clients to have the best terms possible, and this is a significant opportunity.`
    });
  }
  // Equity position
  script.push({
    section: 'Equity Position',
    text: `Another thing worth mentioning — it's been about ${monthsSinceFunding} months since we closed, and between your payments and the way property values have been moving, you're sitting on roughly ${formatUSD(Math.round(totalEquity))} in equity. That puts you in a really strong position, whether you're looking to refinance, tap into that equity, or just have peace of mind knowing your investment is growing.`
  });
  // Close / CTA
  script.push({
    section: 'Close',
    text: `I'd love to run the exact numbers for you — it only takes a few minutes and there's no obligation. Would you have some time this week for a quick 10-minute call where I can walk you through the specifics? I want to make sure you're getting the best deal possible, just like when we worked together the first time.`
  });
  // Objection handling
  script.push({
    section: 'If They Hesitate',
    text: `I completely understand — it's a big decision. How about I just send you a quick email with the numbers so you can look at it on your own time? That way you have all the information and can reach out whenever you're ready. No pressure at all.`
  });
  return script;
}
interface FilterOption {
  value: string;
  label: string;
}
function MultiSelectFilter({
  label,
  options,
  selected,
  onChange
}: {
  label: string;
  options: FilterOption[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);
  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(next);
  };
  const activeCount = selected.size;
  const displayLabel = activeCount === 0 ? label : `${label} (${activeCount})`;
  return <div className="relative" ref={ref} data-id="element-5">
      <button onClick={() => setOpen(!open)} className={`h-9 pl-3 pr-2.5 rounded-lg border text-sm flex items-center gap-1.5 cursor-pointer transition-all whitespace-nowrap ${activeCount > 0 ? 'border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`} data-id="element-6">
        {displayLabel}
        <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-7" />
      </button>

      {open && <div className="absolute top-full left-0 mt-1.5 bg-white rounded-lg border border-gray-200 shadow-lg z-30 min-w-[180px] py-1.5 animate-in fade-in slide-in-from-top-1" data-id="element-8">
          {options.map(option => {
        const isSelected = selected.has(option.value);
        return <button key={option.value} onClick={() => toggle(option.value)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${isSelected ? 'text-blue-700 bg-blue-50/50' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-9">
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`} data-id="element-10">
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-11" />}
                </span>
                {option.label}
              </button>;
      })}
          {activeCount > 0 && <>
              <div className="border-t border-gray-100 my-1" data-id="element-12"></div>
              <button onClick={() => onChange(new Set())} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-13">
                <XIcon className="w-3.5 h-3.5" data-id="element-14" />
                Clear selection
              </button>
            </>}
        </div>}
    </div>;
}
const RATE_OPTIONS: FilterOption[] = [{
  value: 'above-7',
  label: 'Above 7%'
}, {
  value: '6.5-7',
  label: '6.5% – 7%'
}, {
  value: 'below-6.5',
  label: 'Below 6.5%'
}];
const AMOUNT_OPTIONS: FilterOption[] = [{
  value: 'under-300k',
  label: 'Under $300K'
}, {
  value: '300k-500k',
  label: '$300K – $500K'
}, {
  value: 'over-500k',
  label: 'Over $500K'
}];
const DATE_OPTIONS: FilterOption[] = [{
  value: 'last-3',
  label: 'Last 3 months'
}, {
  value: '3-6',
  label: '3–6 months ago'
}, {
  value: 'over-6',
  label: '6+ months ago'
}];
const TYPE_OPTIONS: FilterOption[] = [{
  value: 'Conventional',
  label: 'Conventional'
}, {
  value: 'FHA',
  label: 'FHA'
}, {
  value: 'VA',
  label: 'VA'
}, {
  value: 'Jumbo',
  label: 'Jumbo'
}];
export function BorrowerList() {
  const [borrowers, setBorrowers] = useState<Borrower[]>(INITIAL_BORROWERS);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [scriptBorrowerId, setScriptBorrowerId] = useState<string | null>(null);
  // Filter states — all multi-select
  const [searchQuery, setSearchQuery] = useState('');
  const [rateFilters, setRateFilters] = useState<Set<string>>(new Set());
  const [amountFilters, setAmountFilters] = useState<Set<string>>(new Set());
  const [dateFilters, setDateFilters] = useState<Set<string>>(new Set());
  const [loanTypeFilters, setLoanTypeFilters] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };
  const filteredBorrowers = useMemo(() => {
    return borrowers.filter(borrower => {
      // Search filter
      if (searchQuery && !borrower.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Rate filter (multi-select — match ANY selected)
      if (rateFilters.size > 0) {
        const matchesRate = Array.from(rateFilters).some(filter => {
          if (filter === 'above-7') return borrower.rate > 7;
          if (filter === '6.5-7') return borrower.rate >= 6.5 && borrower.rate <= 7;
          if (filter === 'below-6.5') return borrower.rate < 6.5;
          return false;
        });
        if (!matchesRate) return false;
      }
      // Amount filter (multi-select — match ANY selected)
      if (amountFilters.size > 0) {
        const matchesAmount = Array.from(amountFilters).some(filter => {
          if (filter === 'under-300k') return borrower.loanAmount < 300000;
          if (filter === '300k-500k') return borrower.loanAmount >= 300000 && borrower.loanAmount <= 500000;
          if (filter === 'over-500k') return borrower.loanAmount > 500000;
          return false;
        });
        if (!matchesAmount) return false;
      }
      // Date filter (multi-select — match ANY selected)
      if (dateFilters.size > 0) {
        const fundedDate = new Date(borrower.fundedDate);
        const now = new Date();
        const monthsDiff = (now.getFullYear() - fundedDate.getFullYear()) * 12 + (now.getMonth() - fundedDate.getMonth());
        const matchesDate = Array.from(dateFilters).some(filter => {
          if (filter === 'last-3') return monthsDiff <= 3;
          if (filter === '3-6') return monthsDiff > 3 && monthsDiff <= 6;
          if (filter === 'over-6') return monthsDiff > 6;
          return false;
        });
        if (!matchesDate) return false;
      }
      // Loan type filter (multi-select — match ANY selected)
      if (loanTypeFilters.size > 0) {
        if (!loanTypeFilters.has(borrower.loanType)) return false;
      }
      return true;
    });
  }, [borrowers, searchQuery, rateFilters, amountFilters, dateFilters, loanTypeFilters]);
  const clearFilters = () => {
    setSearchQuery('');
    setRateFilters(new Set());
    setAmountFilters(new Set());
    setDateFilters(new Set());
    setLoanTypeFilters(new Set());
  };
  const hasActiveFilters = searchQuery || rateFilters.size > 0 || amountFilters.size > 0 || dateFilters.size > 0 || loanTypeFilters.size > 0;
  const scriptBorrower = scriptBorrowerId ? borrowers.find(b => b.id === scriptBorrowerId) ?? null : null;
  const callScript = scriptBorrower ? generateCallScript(scriptBorrower) : [];
  return <Card className="h-full flex flex-col" data-id="element-15">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4" data-id="element-16">
        <div data-id="element-17">
          <CardTitle data-id="element-18">Funded Borrowers</CardTitle>
          <p className="text-sm text-gray-600 mt-1" data-id="element-19">
            {filteredBorrowers.length} borrowers eligible for follow-up · Click
            a row for talking points
          </p>
        </div>
      </CardHeader>

      {/* Filter Bar */}
      <div className="px-6 py-3 border-b border-gray-200 bg-gray-50/50 flex flex-col md:flex-row gap-3 items-start md:items-center" data-id="element-20">
        <div className="relative w-full md:w-56 flex-shrink-0" data-id="element-21">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-22" />
          <input type="text" placeholder="Search by name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-9 pl-9 pr-4 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" data-id="element-23" />
        </div>

        <div className="hidden md:block w-px h-6 bg-gray-200 flex-shrink-0" data-id="element-24"></div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto items-center" data-id="element-25">
          <MultiSelectFilter label="Rate" options={RATE_OPTIONS} selected={rateFilters} onChange={setRateFilters} data-id="element-26" />
          <MultiSelectFilter label="Amount" options={AMOUNT_OPTIONS} selected={amountFilters} onChange={setAmountFilters} data-id="element-27" />
          <MultiSelectFilter label="Funded" options={DATE_OPTIONS} selected={dateFilters} onChange={setDateFilters} data-id="element-28" />
          <MultiSelectFilter label="Type" options={TYPE_OPTIONS} selected={loanTypeFilters} onChange={setLoanTypeFilters} data-id="element-29" />

          {hasActiveFilters && <button onClick={clearFilters} className="h-9 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 bg-white transition-colors" data-id="element-30">
              <XIcon className="w-3.5 h-3.5" data-id="element-31" />
              Clear all
            </button>}
        </div>

        <div className="hidden md:block ml-auto text-xs text-gray-500 flex-shrink-0" data-id="element-32">
          Showing {filteredBorrowers.length} of {borrowers.length}
        </div>
      </div>

      <CardContent className="p-0 flex-1" data-id="element-33">
        {filteredBorrowers.length === 0 ? <div className="h-64 flex flex-col items-center justify-center text-center p-8" data-id="element-34">
            <div className="bg-gray-50 p-4 rounded-full mb-4" data-id="element-35">
              <SearchIcon className="w-8 h-8 text-gray-400" data-id="element-36" />
            </div>
            <h3 className="text-lg font-medium text-gray-900" data-id="element-37">
              No borrowers found
            </h3>
            <p className="text-sm text-gray-600 mt-1 max-w-xs mx-auto" data-id="element-38">
              Try adjusting your filters or search query to see more results.
            </p>
            <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4" data-id="element-39">
              Clear Filters
            </Button>
          </div> : <div className="overflow-x-auto" data-id="element-40">
            <table className="w-full text-left text-sm" data-id="element-41">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-42">
                <tr data-id="element-43">
                  <th className="w-10 pl-5 pr-1 py-3" data-id="element-44"></th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-45">
                    Name
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-46">
                    Phone
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-47">
                    Email
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-48">
                    Loan Amount
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-49">
                    Type
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-50">
                    Rate
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-51">
                    Funded
                  </th>
                  <th className="px-3 pr-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-52">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-53">
                {filteredBorrowers.map(borrower => {
              const isExpanded = expandedId === borrower.id;
              const talkingPoints = isExpanded ? getTalkingPoints(borrower) : [];
              return <Fragment key={borrower.id} data-id="element-54">
                      <tr onClick={() => toggleExpand(borrower.id)} className={`
                          cursor-pointer transition-colors select-none
                          ${isExpanded ? 'bg-blue-50/40' : 'hover:bg-gray-50/80'}
                        `} data-id="element-55">
                        <td className="pl-5 pr-1 py-3.5 align-middle" data-id="element-56">
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-blue-500' : ''}`} data-id="element-57" />
                        </td>
                        <td className="px-3 py-3.5 align-middle" data-id="element-58">
                          <div className="font-medium text-gray-900 whitespace-nowrap" data-id="element-59">
                            {borrower.name}
                          </div>
                        </td>
                        <td className="px-3 py-3.5 align-middle" data-id="element-60">
                          <span className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-61">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-62" />
                            {borrower.phone}
                          </span>
                        </td>
                        <td className="px-3 py-3.5 align-middle" data-id="element-63">
                          <span className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-64">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-65" />
                            {borrower.email}
                          </span>
                        </td>
                        <td className="px-3 py-3.5 align-middle text-right" data-id="element-66">
                          <span className="font-medium text-gray-900 whitespace-nowrap tabular-nums" data-id="element-67">
                            {formatUSD(borrower.loanAmount)}
                          </span>
                        </td>
                        <td className="px-3 py-3.5 align-middle text-center" data-id="element-68">
                          <Badge variant="blue" data-id="element-69">{borrower.loanType}</Badge>
                        </td>
                        <td className="px-3 py-3.5 align-middle text-center" data-id="element-70">
                          <Badge variant={borrower.rate > 7 ? 'warning' : 'neutral'} data-id="element-71">
                            {borrower.rate.toFixed(3)}%
                          </Badge>
                        </td>
                        <td className="px-3 py-3.5 align-middle text-center text-gray-600 whitespace-nowrap tabular-nums" data-id="element-72">
                          {new Date(borrower.fundedDate).toLocaleDateString()}
                        </td>
                        <td className="px-3 pr-6 py-3.5 align-middle text-right" data-id="element-73">
                          <div className="flex items-center justify-end gap-2" data-id="element-74">
                            <Button size="sm" variant="primary" onClick={e => {
                        e.stopPropagation();
                        setScriptBorrowerId(borrower.id);
                      }} className="whitespace-nowrap" data-id="element-75">
                              <FileTextIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-76" />
                              Call Script
                            </Button>
                            <Button size="sm" variant="outline" onClick={e => {
                        e.stopPropagation();
                      }} className="whitespace-nowrap" data-id="element-77">
                              View Account
                              <ExternalLinkIcon className="ml-1.5 h-3.5 w-3.5" data-id="element-78" />
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded talking points row */}
                      {isExpanded && <tr className="bg-gradient-to-r from-blue-50/70 via-blue-50/30 to-transparent" data-id="element-79">
                          <td colSpan={9} className="px-0 py-0" data-id="element-80">
                            <div className="py-5 pl-14 pr-6" data-id="element-81">
                              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-3" data-id="element-82">
                                Refinance Talking Points
                              </p>
                              <div className="space-y-3" data-id="element-83">
                                {talkingPoints.map((point, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-84">
                                    <div className="mt-0.5 p-1.5 bg-white rounded-lg shadow-sm border border-gray-100 flex-shrink-0" data-id="element-85">
                                      {point.icon}
                                    </div>
                                    <div className="min-w-0" data-id="element-86">
                                      <p className="font-semibold text-gray-900 text-sm" data-id="element-87">
                                        {point.title}
                                      </p>
                                      <p className="text-gray-600 text-sm leading-relaxed mt-0.5" data-id="element-88">
                                        {point.detail}
                                      </p>
                                    </div>
                                  </div>)}
                              </div>
                              <div className="mt-4 pt-3 border-t border-blue-100/50" data-id="element-89">
                                <p className="text-xs text-gray-500" data-id="element-90">
                                  Based on today's market rate of{' '}
                                  {CURRENT_MARKET_RATE.toFixed(2)}% · Estimates
                                  only, actual terms may vary
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>;
            })}
              </tbody>
            </table>
          </div>}
      </CardContent>

      {/* Call Script Modal */}
      {scriptBorrower && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setScriptBorrowerId(null)} data-id="element-91">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-92">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white" data-id="element-93">
              <div data-id="element-94">
                <div className="flex items-center gap-2" data-id="element-95">
                  <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-96" />
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-97">
                    Call Script
                  </h2>
                </div>
                <p className="text-sm text-gray-600 mt-0.5" data-id="element-98">
                  Personalized for{' '}
                  <span className="font-medium text-blue-600" data-id="element-99">
                    {scriptBorrower.name}
                  </span>{' '}
                  · {scriptBorrower.phone}
                </p>
              </div>
              <button onClick={() => setScriptBorrowerId(null)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-100">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-101" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-102">
              <div className="space-y-6" data-id="element-103">
                {callScript.map((block, idx) => <div key={idx} data-id="element-104">
                    <div className="flex items-center gap-2 mb-2" data-id="element-105">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-106">
                        {idx + 1}
                      </span>
                      <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wider" data-id="element-107">
                        {block.section}
                      </h3>
                    </div>
                    <div className="ml-8 bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-108">
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line" data-id="element-109">
                        "{block.text}"
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-110">
              <p className="text-xs text-gray-500" data-id="element-111">
                Script generated from loan data · Customize as needed
              </p>
              <div className="flex gap-2" data-id="element-112">
                <Button variant="outline" size="sm" onClick={() => setScriptBorrowerId(null)} data-id="element-113">
                  Close
                </Button>
                <Button variant="primary" size="sm" onClick={e => {
              e.stopPropagation();
              // In production: initiate call
            }} data-id="element-114">
                  <PhoneIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-115" />
                  Call {scriptBorrower.name.split(' ')[0]}
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </Card>;
}